import { Paper, Popper, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { DateCalendar } from '@mui/x-date-pickers';
import { BlockStatus } from '@strength/domain';
import { MdClose, MdEvent, MdExpandMore } from '@teamworksdev/icons';
import { PrimitiveAtom } from 'jotai';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DisplayWorkoutStatus,
  isWorkoutStatusReadOnly,
  WorkoutStatusBadge,
} from '../../../components/badge/workout-status-badge';
import { Slot, SlotRenderContentProps } from '../../../components/sheet';
import { CustomSlot } from '../../../components/sheet/slots';
import { CellInputWithAtom } from '../../../components/sheet/slots/common';
import { DATE_PICKER_SLOT } from '../../../components/sheet/slots/slot-labels';
import { cn } from '../../../components/utils';
import { getLocale } from '../../../utils/i18n';
import { jotaiStore } from '../../../utils/jotai';

type DatePickerSlotProps = {
  startDate: string;
  endDate: string;
  x: number;
  y: number;
  width: number;
  height: number;
  value: string | null;
  inputAtom: PrimitiveAtom<string>;
  workoutStatus: DisplayWorkoutStatus;
  blockStatus: BlockStatus;
  allowFlexWorkouts: boolean;
  onValueChange?: (value: string | null) => void;
  error?: boolean;
  disabled?: boolean;
  gridOptions?: {
    style?: React.CSSProperties;
    overrideGridStyles?: boolean;
  };
};

export function datePickerSlot<RawData>({
  startDate,
  endDate,
  x,
  y,
  width,
  height,
  value,
  onValueChange,
  inputAtom,
  workoutStatus,
  blockStatus,
  allowFlexWorkouts,
  error,
  disabled,
  gridOptions,
}: DatePickerSlotProps): Slot<RawData> {
  const isReadOnly = disabled || isWorkoutStatusReadOnly(workoutStatus);
  const canClear = !isReadOnly && allowFlexWorkouts;
  // Captured in onStart so onCommit parses with the same locale/format that was displayed.
  let pendingLocale = getLocale();
  let pendingFormat = getLocaleDateParseFormat(pendingLocale);
  return new CustomSlot(
    { x, y },
    width,
    height,
    false,
    (editing, onBlur) => (
      <DatePickerCellContent
        startDate={startDate}
        endDate={endDate}
        editing={editing}
        inputAtom={inputAtom}
        onValueChange={onValueChange}
        value={value}
        onBlur={onBlur}
        workoutStatus={workoutStatus}
        blockStatus={blockStatus}
        allowFlexWorkouts={allowFlexWorkouts}
        canClear={canClear}
      />
    ),
    {
      className: error ? 'bg-red-100' : 'bg-gray-50',
      selectedBorderStyle: isReadOnly
        ? 'border-2 border-blue-500/50'
        : undefined,
      style: gridOptions?.style,
      overrideGridStyles: gridOptions?.overrideGridStyles,
      editBehavior: isReadOnly
        ? {
            type: 'skip',
          }
        : {
            type: 'input',
            onStart: (input) => {
              pendingLocale = getLocale();
              pendingFormat = getLocaleDateParseFormat(pendingLocale);
              if (input === undefined) {
                jotaiStore.set(
                  inputAtom,
                  isoValueToDisplay(value, '', pendingLocale),
                );
              } else {
                jotaiStore.set(inputAtom, input);
              }
            },
            onCommit: () => {
              const input = jotaiStore.get(inputAtom);
              const parseResult = DateTime.fromFormat(input, pendingFormat, {
                locale: pendingLocale,
              });
              if (parseResult.isValid) {
                onValueChange?.(parseResult.toISODate());
              } else {
                onValueChange?.(null);
              }
              jotaiStore.set(inputAtom, '');
            },
          },
    },
    DATE_PICKER_SLOT,
  );
}

export function getLocaleDateParseFormat(locale: string): string {
  const refDate = DateTime.local(2000, 1, 15).toJSDate(); // Jan 15 — unambiguous month vs day
  const parts = new Intl.DateTimeFormat(locale, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).formatToParts(refDate);
  const firstDateComponent = parts.find(
    ({ type }) => type === 'month' || type === 'day',
  );
  return firstDateComponent?.type === 'month' ? 'M/d/yyyy' : 'd/M/yyyy';
}

export function isoValueToDisplay(
  isoValue: string | null | undefined,
  defaultDisplay: string,
  locale: string = getLocale(),
) {
  const dateTime = DateTime.fromISO(isoValue ?? '').setLocale(locale);
  return dateTime.isValid
    ? dateTime.toLocaleString({
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
    : defaultDisplay;
}

type DatePickerCellContentProps = Omit<
  DatePickerSlotProps,
  'x' | 'y' | 'width' | 'height' | 'disabled'
> &
  Pick<SlotRenderContentProps, 'editing' | 'onBlur'> & {
    canClear: boolean;
  };

function DatePickerCellContent({
  startDate,
  endDate,
  editing,
  inputAtom,
  value,
  workoutStatus,
  blockStatus,
  allowFlexWorkouts: _allowFlexWorkouts,
  canClear,
  onBlur,
  onValueChange,
}: DatePickerCellContentProps) {
  const { t } = useTranslation();
  if (!editing) {
    return (
      <span className="group flex px-2.5 py-2 items-center justify-between w-full">
        {value ? (
          <span className="flex items-center gap-1">
            <Typography variant="body2" color={grey[600]}>
              {isoValueToDisplay(value, t('noDate'))}
            </Typography>
            {canClear && (
              <ClearDateButton
                className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                onClear={() => {
                  if (!canClear) return;
                  onValueChange?.(null);
                }}
              />
            )}
          </span>
        ) : (
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={({ palette: { greyscale } }) => ({
              display: 'flex',
              alignItems: 'center',
              color: greyscale[500],
            })}
          >
            <MdEvent size={15} className="mr-1" />
            {t('assign')}
            <MdExpandMore className="ml-1 mr-2" size={18} color={grey[800]} />
          </Typography>
        )}
        <WorkoutStatusBadge blockStatus={blockStatus} status={workoutStatus} />
      </span>
    );
  }

  return (
    <DatePickerEditMode
      startDate={startDate}
      endDate={endDate}
      inputAtom={inputAtom}
      onBlur={onBlur}
      value={value}
      onValueChange={onValueChange}
      canClear={canClear}
    />
  );
}

type ClearDateButtonProps = {
  onClear: () => void;
  className?: string;
};

function ClearDateButton({ onClear, className }: ClearDateButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center justify-center rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800',
        className,
      )}
      onMouseDown={(e) => {
        // Prevent the cell from entering edit mode on mousedown
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClear();
      }}
    >
      <MdClose size={14} />
    </button>
  );
}

type DatePickerEditModeProps = Omit<
  DatePickerCellContentProps,
  'editing' | 'blockStatus' | 'workoutStatus' | 'allowFlexWorkouts'
>;

function DatePickerEditMode({
  startDate,
  endDate,
  value,
  inputAtom,
  onValueChange,
  onBlur,
  canClear,
}: DatePickerEditModeProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);

  return (
    <>
      <span className="inline-flex items-center gap-1 pl-2.5">
        <span className="inline-block w-[9ch]">
          <CellInputWithAtom
            inputAtom={inputAtom}
            ref={(r) => setAnchorEl(r)}
            // Don't pass onBlur here so that interactions with the Popper don't
            // bump us out of editing
            onBlur={() => {}}
          />
        </span>
        {value && canClear && (
          <ClearDateButton
            onClear={() => {
              if (!canClear) return;
              onValueChange?.(null);
              onBlur(true);
            }}
          />
        )}
      </span>

      <Popper
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement="bottom"
        className="absolute z-50"
        disablePortal
      >
        <Paper onMouseDown={() => {}} className="mt-1" elevation={12}>
          <DateCalendar
            value={value ? DateTime.fromISO(value) : undefined}
            onChange={(value: DateTime<true>) => {
              onValueChange?.(value.toISODate());
              onBlur(true);
            }}
            minDate={DateTime.fromISO(startDate)}
            maxDate={DateTime.fromISO(endDate)}
          />
        </Paper>
      </Popper>
    </>
  );
}
